/**
 * Production-grade authentication service using backend API endpoints.
 * Provides complete audit trails, security controls, and centralized auth management.
 */

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

class AuthService {
  constructor() {
    this.baseURL = `${API_BASE}/api/auth`;
  }

  /**
   * Make authenticated API request with proper error handling
   */
  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    const defaultOptions = {
      credentials: 'include', // Include httpOnly cookies
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    };

    try {
      const response = await fetch(url, { ...defaultOptions, ...options });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      return data;
    } catch (error) {
      console.error(`Auth API Error (${endpoint}):`, error);
      throw error;
    }
  }

  /**
   * Sign up new user with comprehensive logging
   */
  async signUp(email, password, userData = {}) {
    try {
      const response = await this.makeRequest('/signup', {
        method: 'POST',
        body: JSON.stringify({
          email,
          password,
          username: userData.username || null
        })
      });

      return {
        data: response.success ? {
          user: response.user,
          session: response.session
        } : null,
        error: response.success ? null : new Error(response.message)
      };
    } catch (error) {
      return {
        data: null,
        error: error
      };
    }
  }

  /**
   * Sign in existing user with audit trail
   */
  async signIn(email, password) {
    try {
      const response = await this.makeRequest('/login', {
        method: 'POST',
        body: JSON.stringify({
          email,
          password
        })
      });

      if (response.success) {
        // Store tokens from response
        this.setSession(response.session);

        // Trigger auth state change after successful login
        this._triggerAuthStateChange();

        return {
          data: {
            user: response.user,
            session: response.session
          },
          error: null
        };
      } else {
        return {
          data: null,
          error: new Error(response.message)
        };
      }
    } catch (error) {
      return {
        data: null,
        error: error
      };
    }
  }

  /**
   * Sign out current user with logging
   */
  async signOut() {
    try {
      console.log('🔍 Backend Auth Service: signOut called - making request to /logout')
      await this.makeRequest('/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getAccessToken()}`
        }
      });

      // Clear local session after successful logout
      this.clearSession();

      // Trigger auth state change after successful logout
      this._triggerAuthStateChange();

      return { error: null };
    } catch (error) {
      // Even if logout fails on backend, clear local session
      this.clearSession();

      // Still trigger auth state change to update UI
      this._triggerAuthStateChange();

      return { error: error };
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken() {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await this.makeRequest('/refresh', {
        method: 'POST',
        body: JSON.stringify({
          refresh_token: refreshToken
        })
      });

      if (response.success) {
        this.setSession({
          access_token: response.access_token,
          refresh_token: response.refresh_token
        });
        return {
          data: { session: response.session },
          error: null
        };
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      this.clearSession();
      return {
        data: null,
        error: error
      };
    }
  }

  /**
   * Verify current token and get user info
   */
  async verifyToken() {
    try {
      const token = this.getAccessToken();
      if (!token) {
        return { data: null, error: new Error('No access token') };
      }

      const response = await this.makeRequest('/verify', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      return {
        data: { user: response.user },
        error: null
      };
    } catch (error) {
      return {
        data: null,
        error: error
      };
    }
  }

  /**
   * Get current session from server verification using stored access token
   */
  async getSession() {
    try {
      const token = this.getAccessToken();
      if (!token) {
        return { session: null, error: null };
      }

      const response = await this.makeRequest('/verify', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.valid) {
        return {
          session: {
            user: response.user,
            access_token: token,
            refresh_token: this.getRefreshToken(),
            expires_at: null // Server manages expiration
          },
          error: null
        };
      } else {
        // Token is invalid, clear it
        this.clearSession();
        return { session: null, error: null };
      }
    } catch (error) {
      // On error, clear potentially invalid session
      this.clearSession();
      return { session: null, error: null };
    }
  }

  /**
   * Get current user from session
   */
  async getUser() {
    const { session } = await this.getSession();
    return {
      user: session?.user || null,
      error: null
    };
  }

  /**
   * Get access token from localStorage
   */
  getAccessToken() {
    return localStorage.getItem('access_token');
  }

  /**
   * Get refresh token from localStorage
   */
  getRefreshToken() {
    return localStorage.getItem('refresh_token');
  }

  /**
   * Set session data in localStorage
   */
  setSession(sessionData) {
    if (sessionData.access_token) {
      localStorage.setItem('access_token', sessionData.access_token);
    }
    if (sessionData.refresh_token) {
      localStorage.setItem('refresh_token', sessionData.refresh_token);
    }
  }

  /**
   * Clear session data from localStorage
   */
  clearSession() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  /**
   * Auth state change listener (for compatibility with existing code)
   */
  onAuthStateChange(callback) {
    // With httpOnly cookies, we only need to check auth state:
    // 1. On initial load
    // 2. After successful login/logout operations
    // 3. When we get 401 responses from API calls

    const checkAuthState = async () => {
      const { session } = await this.getSession();
      const event = session ? 'SIGNED_IN' : 'SIGNED_OUT';
      callback(event, session);
    };

    // Initial check only
    checkAuthState();

    // Store callback for manual triggers after auth operations
    this._authStateCallback = callback;

    // Return cleanup function
    return {
      data: {
        subscription: {
          unsubscribe: () => {
            this._authStateCallback = null;
          }
        }
      }
    };
  }

  /**
   * Manually trigger auth state check (called after auth operations)
   */
  async _triggerAuthStateChange() {
    if (this._authStateCallback) {
      const { session } = await this.getSession();
      const event = session ? 'SIGNED_IN' : 'SIGNED_OUT';
      this._authStateCallback(event, session);
    }
  }

  /**
   * Check if user is currently authenticated
   */
  async isAuthenticated() {
    const { session } = await this.getSession();
    return !!session?.user;
  }

  /**
   * Get current authentication configuration
   */
  async getLoggingConfig() {
    try {
      const response = await this.makeRequest('/logging-config', {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Failed to get logging config:', error);
      return null;
    }
  }
}

// Create and export singleton instance
const authService = new AuthService();

// Export both the service and helpers for backward compatibility
export { authService };

export const authHelpers = {
  signUp: (email, password, userData) => authService.signUp(email, password, userData),
  signIn: (email, password) => authService.signIn(email, password),
  signOut: () => authService.signOut(),
  getSession: () => authService.getSession(),
  getUser: () => authService.getUser(),
  onAuthStateChange: (callback) => authService.onAuthStateChange(callback)
};

export default authService;